/**
 * Expense Calculation Utilities
 *
 * Pure business logic for expense calculations.
 * These functions have no side effects and can be easily tested.
 */

/**
 * Compare two dates (date-only, ignoring time)
 */
export function compareDateOnly(date1: Date, date2: Date): number {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
  return d1.getTime() - d2.getTime();
}

interface MemberInput {
  memberId: string;
  memberType: "user" | "guest";
  joinedAt: string;
}

interface ShareOutput {
  memberId: string;
  memberType: "user" | "guest";
  amount: number;
}

/**
 * Calculate equal shares for expense splitting
 *
 * This is business logic that belongs in the application layer.
 * It handles:
 * - Filtering eligible members (joined before or on expense date)
 * - Distributing the amount equally
 * - Handling remainder distribution (extra yen goes to first members)
 *
 * @param amount - Total amount to split
 * @param members - Array of members with their join dates
 * @param expenseDate - The date of the expense (for eligibility check)
 * @returns Array of share allocations
 */
export function calculateEqualShares(
  amount: number,
  members: MemberInput[],
  expenseDate: Date
): ShareOutput[] {
  // Filter eligible members (joined before or on expense date)
  const eligibleMembers = members.filter((m) => {
    const joinedAt = new Date(m.joinedAt);
    return compareDateOnly(joinedAt, expenseDate) <= 0;
  });

  if (eligibleMembers.length === 0) {
    return [];
  }

  // Calculate equal shares with remainder distribution
  const shareAmount = Math.floor(amount / eligibleMembers.length);
  const remainder = amount - shareAmount * eligibleMembers.length;

  return eligibleMembers.map((m, index) => ({
    memberId: m.memberId,
    memberType: m.memberType,
    amount: shareAmount + (index < remainder ? 1 : 0),
  }));
}
